import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate, cn } from '@/lib/utils';
import { getCustomerDetail } from '@/features/intelligence/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Tag, Hash, Briefcase, ExternalLink, MessageSquare, Building2,
} from 'lucide-react';
import { getAccountDashboard, updateAccountCalibrationContract, updateAccountOnsiteCalibration, updateAccountCalibrationProvider, updateAccountWhitelisting, updateAccountName } from '../api';
import { useSalesManagers, useUpdateAccountSalesManager } from '../hooks/useSubscriptionManagers';
import type { AccountDashboardData } from '../types';
import {
  HealthScoreCard,
  MetricsGrid,
  SubscriptionTimeline,
  PipelineSummaryCard,
  SubscriptionRenewalCard,
  DealsNeedingAttentionCard,
  QuotePipelineCard,
  RecentActivityCard,
  SubscriptionsTab,
  InvoicesTab,
  InvoiceReminderCard,
  OpportunitiesTab,
  QuotesTab,
  SitesTab,
  SensorsTab,
  InterventionsTab,
  TransmittersTab,
  GatewaysTab,
  TicketsTab,
  CommunicationsTab,
  UsersTab,
  ActivityTab,
  EngagementsTab,
  DocumentsTab,
  NotesTab,
  SettingsTab,
  AccountNotesCard,
  CalibrationSummaryCard,
} from '../components';
import { KeyContactsPanel } from '../components/KeyContactsPanel';
import { CompanyKeyContactsPanel } from '../components/CompanyKeyContactsPanel';
import CallbackRequestsDrawer from '../components/CallbackRequestsDrawer';
import { AgentPanel } from '@/shared/components/AgentPanel';
import { resolveAccountHealth } from '../lib/health';
import { getAccountStatusPresentation } from '../lib/status';
import { useSetPageBreadcrumb } from '@/shared/components/Layout/PageBreadcrumbContext';

const VALID_TABS = [
  'overview',
  'overview2',
  'subscriptions',
  'quotes',
  'invoices',
  'opportunities',
  'users',
  'sites',
  'sensors',
  'interventions',
  'transmitters',
  'gateways',
  'tickets',
  'communications',
  'engagements',
  'activity',
  'notes',
  'settings',
];

export default function AccountDashboardPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'overview2';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [callbackDrawerOpen, setCallbackDrawerOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const accountNameInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Sync tab state with URL query parameter
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams, activeTab]);

  // Update URL when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'overview2') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Guard: accountId must exist (from route parameter)
  if (!accountId) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <p className="text-destructive">
            Invalid account ID. Please check the URL.
          </p>
          <Link to="/accounts">
            <Button variant="outline" className="mt-4">
              Back to Accounts
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['account-dashboard', accountId],
    queryFn: () => getAccountDashboard(accountId, { includeArchived: true }),
    enabled: !!accountId,
  });

  // Surface the account in the admin header (e.g. "ACCOUNTS / 0000-01 OPTILAB Outaouais")
  useSetPageBreadcrumb(
    data?.account
      ? `${data.account.account_number} ${data.account.name}`
      : null,
  );

  const hasLegacyIntelligenceSource = Boolean(
    data?.account.mongo_organisation_id || data?.account.organisation_id
  );

  const { data: customerHealthDetail } = useQuery({
    queryKey: ['intelligence', 'customers', 'detail', accountId, false],
    queryFn: () => getCustomerDetail(accountId),
    enabled: !!accountId && !!data && !data.account.archived_at && hasLegacyIntelligenceSource,
    staleTime: 5 * 60 * 1000,
  });

  // Mutation to update calibration contract flag
  const calibrationMutation = useMutation({
    mutationFn: (value: 'true' | 'false' | 'other') =>
      updateAccountCalibrationContract(accountId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
    },
  });

  // Mutation to update onsite calibration flag
  const onsiteCalibrationMutation = useMutation({
    mutationFn: (value: 'true' | 'false' | 'other') =>
      updateAccountOnsiteCalibration(accountId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
    },
  });

  // Mutation to update whitelisting (decloisonnement) flag
  const whitelistingMutation = useMutation({
    mutationFn: (value: 'true' | 'false' | 'other') =>
      updateAccountWhitelisting(accountId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
    },
  });

  // Mutation to update calibration provider (who calibrates: atek / client / other)
  const calibrationProviderMutation = useMutation({
    mutationFn: (value: 'atek' | 'client' | 'other') =>
      updateAccountCalibrationProvider(accountId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
    },
  });

  const accountNameMutation = useMutation({
    mutationFn: (name: string) => {
      const mongoOrganisationId = data?.account.mongo_organisation_id;
      if (!mongoOrganisationId) {
        throw new Error('This account is not linked to a legacy MongoDB organisation.');
      }

      return updateAccountName(accountId, mongoOrganisationId, name);
    },
    onMutate: async (name: string) => {
      const trimmedName = name.trim();
      const previousDashboard = queryClient.getQueryData<AccountDashboardData>(['account-dashboard', accountId]);
      const previousAccount = queryClient.getQueryData(['account', accountId]);

      queryClient.setQueryData<AccountDashboardData>(
        ['account-dashboard', accountId],
        (current) => (current ? { ...current, account: { ...current.account, name: trimmedName } } : current),
      );
      queryClient.setQueryData(
        ['account', accountId],
        (current: { name?: string } | undefined) => (current ? { ...current, name: trimmedName } : current),
      );

      return { previousDashboard, previousAccount };
    },
    onSuccess: (result, requestedName) => {
      const trimmedName = requestedName.trim();
      const updatedAccount = result.account
        ?? queryClient.getQueryData<AccountDashboardData>(['account-dashboard', accountId])?.account;

      if (updatedAccount) {
        queryClient.setQueryData<AccountDashboardData>(
          ['account-dashboard', accountId],
          (current) => (current ? { ...current, account: { ...updatedAccount, name: trimmedName } } : current),
        );
        queryClient.setQueryData(['account', accountId], { ...updatedAccount, name: trimmedName });
      }

      if (result.synced) {
        queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
        queryClient.invalidateQueries({ queryKey: ['account', accountId] });
        queryClient.invalidateQueries({ queryKey: ['accounts-table'] });
        queryClient.invalidateQueries({ queryKey: ['accounts'] });
        toast.success('Account name updated');
      } else {
        toast.success('Account name updated in MongoDB. PostgreSQL sync is still in progress.');
      }

      setIsEditingName(false);
    },
    onError: (error, _requestedName, context) => {
      if (context?.previousDashboard) {
        queryClient.setQueryData(['account-dashboard', accountId], context.previousDashboard);
      }
      if (context?.previousAccount) {
        queryClient.setQueryData(['account', accountId], context.previousAccount);
      }
      toast.error(error instanceof Error ? error.message : 'Failed to update account name');
    },
  });

  // Account Manager mutation (assigned directly to account)
  const accountManagerMutation = useUpdateAccountSalesManager(accountId);

  useEffect(() => {
    if (!isEditingName) {
      setEditedName(data?.account.name || '');
    }
  }, [data?.account.name, isEditingName]);

  useEffect(() => {
    if (isEditingName) {
      accountNameInputRef.current?.focus();
      accountNameInputRef.current?.select();
    }
  }, [isEditingName]);

  useEffect(() => {
    if ((data?.account.archived_at ?? null) && activeTab !== 'settings') {
      setActiveTab('settings');
      searchParams.set('tab', 'settings');
      setSearchParams(searchParams, { replace: true });
    }
  }, [activeTab, data?.account.archived_at, searchParams, setSearchParams]);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <p className="text-destructive">
            Error loading account. Please try again.
          </p>
          <Link to="/accounts">
            <Button variant="outline" className="mt-4">
              Back to Accounts
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const { account, health, metrics: dashboardMetrics, current_subscription, subscription_history, alerts, has_renewal_opportunity, quotes, activity_summary } =
    data;
  const current_contract = current_subscription;
  const isArchivedAccount = !!account.archived_at;
  const effectiveActiveTab = isArchivedAccount
    ? (activeTab === 'settings' ? 'settings' : 'settings')
    : activeTab;
  const displayedAccountManagerId = account.sales_manager_id ?? current_contract?.sales_manager_id;
  const displayedAccountManagerName = account.sales_manager_name ?? current_contract?.sales_manager_name;
  const statusPresentation = getAccountStatusPresentation(account.status);

  const metrics = dashboardMetrics;
  const resolvedHealth = resolveAccountHealth(health, customerHealthDetail);

  const handleStartEditingName = () => {
    setEditedName(account.name);
    setIsEditingName(true);
  };

  const handleCancelEditingName = () => {
    setEditedName(account.name);
    setIsEditingName(false);
  };

  const handleSaveAccountName = async () => {
    const trimmedName = editedName.trim();

    if (!trimmedName) {
      toast.error('Account name cannot be empty');
      return;
    }

    if (trimmedName === account.name) {
      setIsEditingName(false);
      return;
    }

    await accountNameMutation.mutateAsync(trimmedName);
  };

  const accountHeader = (
    <div className="flex items-start justify-between">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          {isEditingName ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                ref={accountNameInputRef}
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="h-9 min-w-[240px] max-w-[420px] text-lg font-semibold"
                aria-label="Account name"
                disabled={accountNameMutation.isPending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleSaveAccountName();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    handleCancelEditingName();
                  }
                }}
              />
              <Button
                size="sm"
                onClick={() => void handleSaveAccountName()}
                disabled={accountNameMutation.isPending}
              >
                {accountNameMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelEditingName}
                disabled={accountNameMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          ) : isArchivedAccount ? (
            <h1 className="text-lg font-semibold text-foreground">
              {account.name}
            </h1>
          ) : (
            <h1 className="text-lg font-semibold text-foreground">
              <button
                type="button"
                className="text-left transition-colors hover:text-atek-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                onClick={handleStartEditingName}
                title="Click to edit account name"
              >
                {account.name}
              </button>
            </h1>
          )}
          <Badge variant={statusPresentation.badgeVariant}>
            {account.status}
          </Badge>
        </div>

        {/* Compacted functional metadata strip: one row of label/value cells,
            divider between each. Edit-in-place selects sit inside their cell. */}
        <div className="mt-3 flex flex-wrap items-stretch gap-y-2 divide-x divide-border">
          <StatCell label="Account #">
            <span className="font-mono">{account.account_number}</span>
          </StatCell>
          <StatCell label="Tier">{account.tier || 'Standard'}</StatCell>
          <StatCell label={statusPresentation.lifecycleLabel}>
            {(account.first_subscription_date || account.first_contract_date)
              ? formatDate((account.first_subscription_date || account.first_contract_date)!)
              : 'N/A'}
          </StatCell>
          {isArchivedAccount ? (
            <StatCell label="State">Archived</StatCell>
          ) : (
            <>
              <StatCell label="Calibration">
                <Select
                  value={account.has_calibration_subscription || account.has_calibration_contract || 'false'}
                  onValueChange={(value: 'true' | 'false' | 'other') =>
                    calibrationMutation.mutate(value)
                  }
                  disabled={calibrationMutation.isPending}
                >
                  <SelectTrigger className={cn(
                    'h-6 w-auto gap-1 border-0 px-1.5 py-0 text-xs font-medium',
                    (account.has_calibration_subscription || account.has_calibration_contract) === 'true'
                      ? 'bg-green-100 text-green-800'
                      : (account.has_calibration_subscription || account.has_calibration_contract) === 'other'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-600',
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </StatCell>
              <StatCell label="Onsite calibration">
                <Select
                  value={account.has_onsite_calibration || 'false'}
                  onValueChange={(value: 'true' | 'false' | 'other') =>
                    onsiteCalibrationMutation.mutate(value)
                  }
                  disabled={onsiteCalibrationMutation.isPending}
                >
                  <SelectTrigger className={cn(
                    'h-6 w-auto gap-1 border-0 px-1.5 py-0 text-xs font-medium',
                    account.has_onsite_calibration === 'true'
                      ? 'bg-green-100 text-green-800'
                      : account.has_onsite_calibration === 'other'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-600',
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </StatCell>
              <StatCell label="Calibrated by">
                <Select
                  value={account.calibration_provider || 'other'}
                  onValueChange={(value: 'atek' | 'client' | 'other') =>
                    calibrationProviderMutation.mutate(value)
                  }
                  disabled={calibrationProviderMutation.isPending}
                >
                  <SelectTrigger className={cn(
                    'h-6 w-auto gap-1 border-0 px-1.5 py-0 text-xs font-medium',
                    account.calibration_provider === 'atek'
                      ? 'bg-green-100 text-green-800'
                      : account.calibration_provider === 'client'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-amber-100 text-amber-800',
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="atek">ATEK</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="other">To verify</SelectItem>
                  </SelectContent>
                </Select>
              </StatCell>
              <StatCell label="Whitelisting">
                <Select
                  value={account.requires_whitelisting || 'false'}
                  onValueChange={(value: 'true' | 'false' | 'other') =>
                    whitelistingMutation.mutate(value)
                  }
                  disabled={whitelistingMutation.isPending}
                >
                  <SelectTrigger className={cn(
                    'h-6 w-auto gap-1 border-0 px-1.5 py-0 text-xs font-medium',
                    account.requires_whitelisting === 'true'
                      ? 'bg-green-100 text-green-800'
                      : account.requires_whitelisting === 'other'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-600',
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </StatCell>
              <StatCell label="Account Mgr">
                <SalesManagerSelect
                  currentId={displayedAccountManagerId || undefined}
                  currentName={displayedAccountManagerName || undefined}
                  onSelect={(id) => accountManagerMutation.mutate(id || null)}
                  disabled={accountManagerMutation.isPending}
                />
              </StatCell>
              <StatCell label="Subscription">
                {current_contract ? `#${current_contract.contract_number}` : 'None'}
              </StatCell>
            </>
          )}
        </div>
      </div>
      {!isArchivedAccount && <KeyContactsPanel accountId={accountId} />}
    </div>
  );

  // Group alerts by the tab they belong to (e.g. overdue_invoices → invoices).
  // Used to render a flashing red dot on the tab label instead of a top banner.
  const ALERT_TYPE_TO_TAB: Record<string, string> = {
    overdue_invoices: 'invoices',
    contract_expiring: 'subscriptions',
  };
  const tabAlerts: Record<string, AccountDashboardData['alerts']> = {};
  const nonTabAlerts: AccountDashboardData['alerts'] = [];
  for (const a of alerts) {
    const tab = ALERT_TYPE_TO_TAB[a.type];
    if (tab) {
      (tabAlerts[tab] ||= []).push(a);
    } else {
      nonTabAlerts.push(a);
    }
  }
  const callbackAlert = nonTabAlerts.find((a) => a.type === 'pending_callback_requests');

  const tabLabel = (key: string, label: string) => {
    const tabAlertsForKey = tabAlerts[key];
    if (!tabAlertsForKey?.length) return label;
    const top = tabAlertsForKey[0];
    const severityClass =
      top.severity === 'critical' ? 'bg-red-500' : 'bg-amber-500';
    return (
      <span className="inline-flex items-center gap-1.5">
        {label}
        <span
          className={cn('inline-block h-2 w-2 rounded-full animate-pulse', severityClass)}
          title={tabAlertsForKey.map((a) => a.message).join(' · ')}
          aria-label={tabAlertsForKey.map((a) => a.message).join(' · ')}
        />
      </span>
    );
  };

  return (
    <div className="space-y-5 accounts-tables-font">
      {/* Archived accounts can't reach the Overview tab, so render the header
          above the tabs instead — otherwise the account name + status would
          never be visible. */}
      {isArchivedAccount && accountHeader}

      {/* Tabs */}
      <Tabs value={effectiveActiveTab} onValueChange={handleTabChange} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            {!isArchivedAccount && <TabsTrigger value="overview">Overview</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="overview2">Overview 2</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="subscriptions">{tabLabel('subscriptions', 'Subscriptions')}</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="quotes">Quotes</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="invoices">{tabLabel('invoices', 'Invoices')}</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="sensors">Sensors</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="interventions">Interventions</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="transmitters">Transmitters</TabsTrigger>}
            {!isArchivedAccount && metrics.total_gateways > 0 && <TabsTrigger value="gateways">Gateways</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="users">Users</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="sites">Sites</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="tickets">Tickets</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="communications">Notifications</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="engagements">Engagements</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="documents">Documents</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="notes">Notes</TabsTrigger>}
            {!isArchivedAccount && <TabsTrigger value="activity">Activity</TabsTrigger>}
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          {!isArchivedAccount && callbackAlert && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCallbackDrawerOpen(true)}
              className="text-blue-700 hover:text-blue-900"
              title={callbackAlert.message}
            >
              <span className="inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse mr-1.5" />
              {callbackAlert.message}
            </Button>
          )}
        </div>

        <TabsContent value="overview" className="space-y-4">
          {accountHeader}

          {/* Compact metrics row: Health Score + key counters */}
          <div className="grid gap-3 md:grid-cols-4">
            <HealthScoreCard health={resolvedHealth} to={`/intelligence/customers/${account.id}`} />
            <MetricsGrid metrics={metrics} onNavigateToTab={handleTabChange} />
          </div>

          {/* Recent Notes + Deals Needing Attention + Calibration Certificates */}
          <div className="grid gap-4 md:grid-cols-3">
            <AccountNotesCard
              accountId={accountId}
              onNavigateToTab={handleTabChange}
            />
            <DealsNeedingAttentionCard
              accountId={accountId}
              onNavigateToTab={handleTabChange}
            />
            {((account.has_calibration_subscription || account.has_calibration_contract) === 'true' || (account.has_calibration_subscription || account.has_calibration_contract) === 'other') ? (
              <CalibrationSummaryCard accountId={accountId} />
            ) : (
              <div />
            )}
          </div>

          {/* Quote Pipeline + Pipeline Summary */}
          <div className="grid gap-4 md:grid-cols-2">
            <QuotePipelineCard
              quotes={quotes}
              onNavigateToTab={handleTabChange}
            />

            {/* Pipeline Summary */}
            <PipelineSummaryCard
              accountId={accountId}
              onNavigateToTab={handleTabChange}
            />
          </div>

          {/* Contract Renewal + Contract Timeline */}
          <div className="grid gap-4 md:grid-cols-2">
            <SubscriptionRenewalCard
              accountId={accountId}
              contract={current_subscription}
              hasRenewalOpportunity={has_renewal_opportunity}
              onNavigateToTab={handleTabChange}
            />
            <SubscriptionTimeline history={subscription_history} />
          </div>

          {/* Recent Activity */}
          <RecentActivityCard
            activitySummary={activity_summary}
            contractDaysRemaining={current_contract ? Math.ceil((new Date(current_contract.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null}
            hasOpenDeals={true}
            onNavigateToTab={handleTabChange}
          />
        </TabsContent>

        <TabsContent value="overview2" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Left: Account Info sidebar */}
            <div className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Account Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <Overview2InfoRow
                    label="Type"
                    icon={<Tag className="h-3.5 w-3.5" />}
                    value={<Badge variant="outline">{account.type}</Badge>}
                  />
                  <Overview2InfoRow
                    label="Status"
                    icon={<Hash className="h-3.5 w-3.5" />}
                    value={<Badge variant={statusPresentation.badgeVariant}>{account.status}</Badge>}
                  />
                  <Overview2InfoRow
                    label="Tier"
                    icon={<Briefcase className="h-3.5 w-3.5" />}
                    value={account.tier || 'Standard'}
                  />
                  <Overview2InfoRow
                    label="Account #"
                    icon={<Hash className="h-3.5 w-3.5" />}
                    value={<span className="font-mono">{account.account_number}</span>}
                  />
                  <Overview2InfoRow
                    label={statusPresentation.lifecycleLabel}
                    value={(account.first_subscription_date || account.first_contract_date)
                      ? formatDate((account.first_subscription_date || account.first_contract_date)!)
                      : null}
                  />
                  <Overview2InfoRow
                    label="Account Manager"
                    value={displayedAccountManagerName || null}
                  />
                  <Overview2InfoRow
                    label="Calibration"
                    value={account.has_calibration_subscription || account.has_calibration_contract || 'false'}
                  />
                  <Overview2InfoRow
                    label="Whitelisting"
                    value={account.requires_whitelisting || 'false'}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Center: Activity */}
            <div className="lg:col-span-3">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p>No activities yet</p>
                    <p className="text-xs mt-1">Hook this up to the account timeline next.</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right: Contacts + Links */}
            <div className="lg:col-span-1 space-y-4">
              <CompanyKeyContactsPanel accountId={accountId} />

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1.5">Linked Organisation</div>
                    {account.mongo_organisation_id ? (
                      <div className="flex items-center gap-2 p-2 rounded border bg-muted/30">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm font-mono truncate" title={account.mongo_organisation_id}>
                          {account.organisation_number || account.mongo_organisation_id.slice(-8)}
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No Mongo organisation linked</p>
                    )}
                  </div>

                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1.5">External Links</div>
                    <div className="space-y-1.5">
                      {account.hubspot_company_id && (
                        <a
                          href={`https://app.hubspot.com/contacts/22484617/record/0-2/${account.hubspot_company_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-primary hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> HubSpot
                        </a>
                      )}
                      {account.quickbooks_customer_id && (
                        <a
                          href={`https://app.qbo.intuit.com/app/customerdetail?nameId=${account.quickbooks_customer_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-primary hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> QuickBooks
                        </a>
                      )}
                      {account.gdrive_root_folder_id && (
                        <a
                          href={`https://drive.google.com/drive/folders/${account.gdrive_root_folder_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-primary hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Google Drive
                        </a>
                      )}
                      {!account.hubspot_company_id && !account.quickbooks_customer_id && !account.gdrive_root_folder_id && (
                        <p className="text-sm text-muted-foreground">No external links</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="subscriptions">
          <SubscriptionsTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="invoices" className="space-y-4">
          <InvoiceReminderCard accountId={accountId} />
          <InvoicesTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="opportunities">
          <OpportunitiesTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="quotes">
          <QuotesTab accountId={accountId} highlightId={searchParams.get('highlight') || undefined} />
        </TabsContent>

        <TabsContent value="users">
          <UsersTab
            accountId={accountId}
            organisationId={account.mongo_organisation_id}
          />
        </TabsContent>

        <TabsContent value="sites">
          <SitesTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="sensors">
          <SensorsTab
            accountId={accountId}
            metrics={metrics}
            accountName={account.name}
            legacyOrganisationId={account.mongo_organisation_id}
            canImportSensors={Boolean(account.mongo_organisation_id || account.organisation_id)}
          />
        </TabsContent>

        <TabsContent value="interventions">
          <InterventionsTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="transmitters">
          <TransmittersTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="gateways">
          <GatewaysTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="tickets">
          <TicketsTab
            accountId={accountId}
            metrics={metrics}
          />
        </TabsContent>

        <TabsContent value="communications">
          <CommunicationsTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="engagements">
          <EngagementsTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="activity">
          <ActivityTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="notes">
          <NotesTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsTab accountId={accountId} />
        </TabsContent>

        <TabsContent value="settings">
          {isArchivedAccount && (
            <Card>
              <CardContent className="py-4 text-sm text-muted-foreground">
                This account is archived. Linked entities are hidden from account-backed admin pages until the account is unarchived.
              </CardContent>
            </Card>
          )}
          <SettingsTab accountId={accountId} />
        </TabsContent>
      </Tabs>

      {/* Callback Requests Drawer */}
      <CallbackRequestsDrawer
        accountId={accountId}
        open={callbackDrawerOpen}
        onOpenChange={setCallbackDrawerOpen}
        onRequestsUpdated={() => {
          // Refresh dashboard to update alert count
          queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
        }}
      />

      {/* Account Agent — dedicated per-account assistant.
          resourceId scopes Mastra working memory to this account so the agent
          accumulates context across every conversation about this customer. */}
      <AgentPanel
        agentId="account-agent"
        name="Account Agent"
        tagline={`${account.name} (${account.account_number})`}
        intro={`Dedicated agent for ${account.name}. I keep a running profile of this account and can pull subscriptions, AR, tickets, opportunities, and recent comms. Ask me anything about ${account.name}.`}
        placeholder={`Ask about ${account.name}...`}
        variant="purple"
        storagePrefix={`atek-account-agent-${accountId}-`}
        defaultPosition={{ right: 24, bottom: 96 }}
        resourceId={`account-${accountId}`}
        contextPrimer={`Active account context: ${account.name} (account_number ${account.account_number}, accountId ${accountId}). Every question in this thread is about this account. If you need to call am-* tools, use accountId="${accountId}" — do not call am-resolve-account, the account is already known.`}
      />
    </div>
  );
}

// =============================================================================
// Inline Manager Components
// =============================================================================

/**
 * Tight label-over-value cell for the compact account header strip.
 * Designed to sit in a row with `divide-x` siblings.
 */
function StatCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 px-3 first:pl-0 last:pr-0 min-h-[2.5rem] justify-between">
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="text-sm leading-tight">{children}</div>
    </div>
  );
}

function SalesManagerSelect({
  currentId,
  currentName,
  onSelect,
  disabled,
}: {
  currentId?: string;
  currentName?: string;
  onSelect: (id: string) => void;
  disabled: boolean;
}) {
  const { data: managers } = useSalesManagers();
  const [pendingId, setPendingId] = useState<string | null>(null);

  useEffect(() => {
    if (!disabled) setPendingId(null);
  }, [disabled]);

  const resolveManagerName = (id?: string | null, name?: string | null) => name
    || managers?.find((manager) => manager.id === id)?.name
    || 'Unassigned';
  const optimisticId = pendingId === null ? currentId : (pendingId !== '__none__' ? pendingId : null);
  const displayedName = resolveManagerName(optimisticId, pendingId === null ? currentName : undefined);
  const selectValue = pendingId ?? currentId ?? '__none__';

  return (
    <span className="inline-flex items-center gap-1.5">
      <Select
        value={selectValue}
        onValueChange={(value) => {
          if (value === '' || value === selectValue) return;
          setPendingId(value);
          onSelect(value === '__none__' ? '' : value);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          className="h-5 w-auto gap-1 border-0 px-1.5 py-0 text-xs font-medium bg-blue-50 text-blue-700"
        >
          <SelectValue placeholder="Unassigned">
            {displayedName}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">Unassigned</SelectItem>
          {/* Assignment dropdown: only currently-active sales managers.
              `(former)` entries (sales_manager role revoked) stay in the
              filter dropdowns so historical accounts remain searchable, but
              they should not be assignable to new/updated accounts.
              Exception: keep the currently-assigned manager visible even if
              they're now `(former)` so the field doesn't render as empty. */}
          {managers
            ?.filter((m) => !m.isFormer || m.id === currentId)
            .map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.name}{m.isFormer ? ' (former)' : ''}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </span>
  );
}

function Overview2InfoRow({
  label,
  value,
  icon,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
}) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
      {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm break-words">{value}</div>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-9 w-64" />
        <Skeleton className="mt-2 h-5 w-48" />
      </div>
      <div className="grid gap-4 md:grid-cols-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}
